import {
	useCallback,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
	UnsavedChangesContext,
	type ConfirmUnsavedChanges,
} from "@/hooks/unsavedChangesDialog";

type PendingConfirmation = {
	message: string;
	resolve: (confirmed: boolean) => void;
};

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	const [pending, setPending] = useState<PendingConfirmation | null>(null);
	const [activeGuardIds, setActiveGuardIds] = useState<Set<symbol>>(
		() => new Set(),
	);

	const confirmUnsavedChanges = useCallback<ConfirmUnsavedChanges>(
		(message) =>
			new Promise((resolve) => {
				setPending({ message, resolve });
			}),
		[],
	);

	const settle = useCallback((confirmed: boolean) => {
		setPending((current) => {
			current?.resolve(confirmed);
			return null;
		});
	}, []);

	const registerGuard = useCallback((id: symbol, enabled: boolean) => {
		setActiveGuardIds((current) => {
			const next = new Set(current);
			if (enabled) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
		return () => {
			setActiveGuardIds((current) => {
				if (!current.has(id)) return current;
				const next = new Set(current);
				next.delete(id);
				return next;
			});
		};
	}, []);

	const value = useMemo(
		() => ({
			confirmUnsavedChanges,
			hasUnsavedChanges: activeGuardIds.size > 0,
			registerGuard,
		}),
		[activeGuardIds.size, confirmUnsavedChanges, registerGuard],
	);

	return (
		<UnsavedChangesContext.Provider value={value}>
			{children}
			{pending && (
				<ConfirmDialog
					title={t("common.unsavedChangesTitle")}
					message={pending.message}
					confirmLabel={t("common.leaveWithoutSaving")}
					cancelLabel={t("common.cancel")}
					destructive
					onConfirm={() => settle(true)}
					onCancel={() => settle(false)}
				/>
			)}
		</UnsavedChangesContext.Provider>
	);
}
